import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';

/**
 * useSystemData — gathers Finance, Health, and Planner roll-ups for the
 * current user. Each block fails independently so a single missing
 * permission doesn't blank the whole page.
 *
 * Returns:
 *   {
 *     userId,
 *     finance: { incomeMTD, expensesMTD, balance, topSpend, deltaPct },
 *     health:  { waterMl, exerciseMin, mealsCount },
 *     planner: { dueToday, completedToday, upcomingThisWeek, overdue },
 *     loading,
 *     error,
 *     refresh,
 *   }
 */
export default function useSystemData() {
  const [userId, setUserId] = useState(null);
  const [finance, setFinance] = useState(EMPTY_FINANCE);
  const [health, setHealth] = useState(EMPTY_HEALTH);
  const [planner, setPlanner] = useState(EMPTY_PLANNER);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData?.user?.id || null;
      setUserId(uid);
      if (!uid) {
        setFinance(EMPTY_FINANCE);
        setHealth(EMPTY_HEALTH);
        setPlanner(EMPTY_PLANNER);
        return;
      }

      const [financeRes, healthRes, plannerRes] = await Promise.allSettled([
        loadFinance(uid),
        loadHealth(uid),
        loadPlanner(uid),
      ]);

      setFinance(financeRes.status === 'fulfilled' ? financeRes.value : EMPTY_FINANCE);
      setHealth(healthRes.status === 'fulfilled' ? healthRes.value : EMPTY_HEALTH);
      setPlanner(plannerRes.status === 'fulfilled' ? plannerRes.value : EMPTY_PLANNER);

      // Surface the first error (informational only)
      const firstReject = [financeRes, healthRes, plannerRes].find(
        (r) => r.status === 'rejected'
      );
      if (firstReject) setError(firstReject.reason?.message || 'Partial load failure');
    } catch (e) {
      setError(e?.message || 'Failed to load system data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    userId,
    finance,
    health,
    planner,
    loading,
    error,
    refresh: load,
  };
}

/* ================================================================
   FINANCE — money_logs (current month vs previous month)
   ================================================================ */

const EMPTY_FINANCE = {
  incomeMTD: 0,
  expensesMTD: 0,
  balance: 0,
  topSpend: null, // { category, amount }
  deltaPct: null, // % change vs prev month
};

async function loadFinance(uid) {
  const today = new Date();
  const monthStart = startOfMonth(today);
  const prevMonthStart = startOfMonth(addMonths(today, -1));
  const prevMonthEnd = endOfMonth(addMonths(today, -1));

  const { data, error } = await supabase
    .from('money_logs')
    .select('amount, log_type, category, date')
    .gte('date', dateStr(prevMonthStart))
    .lte('date', dateStr(endOfMonth(today)));
  if (error) throw error;

  const rows = data || [];
  const inMonth = (r, start, end) =>
    r.date >= dateStr(start) && r.date <= dateStr(end);

  const cur = rows.filter((r) => inMonth(r, monthStart, endOfMonth(today)));
  const prev = rows.filter((r) => inMonth(r, prevMonthStart, prevMonthEnd));

  const sum = (arr, type) =>
    arr
      .filter((r) => r.log_type === type)
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const incomeMTD = sum(cur, 'income');
  const expensesMTD = sum(cur, 'expense');
  const expensesPrev = sum(prev, 'expense');

  // Top spend category MTD
  const byCat = {};
  for (const r of cur) {
    if (r.log_type !== 'expense') continue;
    const k = r.category || 'Other';
    byCat[k] = (byCat[k] || 0) + (Number(r.amount) || 0);
  }
  let topSpend = null;
  for (const [category, amount] of Object.entries(byCat)) {
    if (!topSpend || amount > topSpend.amount) topSpend = { category, amount };
  }

  const deltaPct =
    expensesPrev > 0
      ? Math.round(((expensesMTD - expensesPrev) / expensesPrev) * 100)
      : null;

  return {
    incomeMTD,
    expensesMTD,
    balance: incomeMTD - expensesMTD,
    topSpend,
    deltaPct,
  };
}

/* ================================================================
   HEALTH — health_logs (today only)
   ================================================================ */

const EMPTY_HEALTH = { waterMl: 0, exerciseMin: 0, mealsCount: 0 };

async function loadHealth(uid) {
  const today = dateStr(new Date());
  const { data, error } = await supabase
    .from('health_logs')
    .select('log_type, log_data, date')
    .eq('date', today);
  if (error) throw error;

  const rows = data || [];
  let waterMl = 0;
  let exerciseMin = 0;
  let mealsCount = 0;
  for (const r of rows) {
    if (r.log_type === 'water') waterMl += Number(r.log_data?.amount_ml) || 0;
    else if (r.log_type === 'exercise') exerciseMin += Number(r.log_data?.duration_min) || 0;
    else if (r.log_type === 'food') mealsCount += 1;
  }
  return { waterMl, exerciseMin, mealsCount };
}

/* ================================================================
   PLANNER — planner_tasks (today + this week)
   ================================================================ */

const EMPTY_PLANNER = {
  dueToday: 0,
  completedToday: 0,
  upcomingThisWeek: 0,
  overdue: 0,
};

async function loadPlanner(uid) {
  const today = new Date();
  const todayStr = dateStr(today);
  const weekEndStr = dateStr(addDays(today, 7));
  const weekStartStr = dateStr(addDays(today, -7));

  const { data, error } = await supabase
    .from('planner_tasks')
    .select('date, is_completed')
    .gte('date', weekStartStr)
    .lte('date', weekEndStr);
  if (error) throw error;

  const rows = data || [];
  let dueToday = 0;
  let completedToday = 0;
  let upcomingThisWeek = 0;
  let overdue = 0;
  for (const r of rows) {
    if (r.date === todayStr) {
      if (r.is_completed) completedToday += 1;
      else dueToday += 1;
    } else if (r.date > todayStr && r.date <= weekEndStr) {
      if (!r.is_completed) upcomingThisWeek += 1;
    } else if (r.date < todayStr && !r.is_completed) {
      overdue += 1;
    }
  }
  return { dueToday, completedToday, upcomingThisWeek, overdue };
}

/* ================================================================
   helpers
   ================================================================ */

function dateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
